// Locale-aware, smart-collapsing date-range formatter for activity cards.
// Rules (JA is user-facing, EN is for internal review):
//   single day      JA 6月26日           EN Jun 26
//   same-month rng  JA 6月26〜28日       EN Jun 26–28
//   cross-month rng JA 6月26日〜7月1日   EN Jun 26 – Jul 1
//   year shown only when it differs from the current (JST) year:
//                   JA 2027年1月3日       EN Jan 3 2027
const EN_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

interface IYmd { y: number; m: number; d: number }
const parse = (iso: string): IYmd => {
  const [y, m, d] = iso.split('-').map(Number);
  return { y: y!, m: m!, d: d! };
};

function single(p: IYmd, ja: boolean, showYear: boolean): string {
  if (ja) return showYear ? `${p.y}年${p.m}月${p.d}日` : `${p.m}月${p.d}日`;
  return showYear ? `${EN_MONTHS[p.m - 1]} ${p.d} ${p.y}` : `${EN_MONTHS[p.m - 1]} ${p.d}`;
}

/**
 * @param start  'YYYY-MM-DD' (activity start), or undefined
 * @param end    'YYYY-MM-DD' (activity end), or undefined
 * @param lang   i18n language ('ja' / 'en' / 'en-US' …) — treated JA iff it starts with 'ja'
 * @param currentYear the current JST year, so the year is hidden when it matches
 */
export function formatDateRange(
  start: string | undefined,
  end: string | undefined,
  lang: string,
  currentYear: number,
): string {
  const ja = lang.toLowerCase().startsWith('ja');
  const s = start ? parse(start) : undefined;
  const e = end ? parse(end) : undefined;

  // Single date: one side missing, or both identical.
  if (s && (!e || start === end)) return single(s, ja, s.y !== currentYear);
  if (e && !s) return single(e, ja, e.y !== currentYear);
  if (!s || !e) return '';

  const showYear = s.y !== currentYear || e.y !== currentYear;
  const sameYear = s.y === e.y;
  const sameMonth = sameYear && s.m === e.m;

  if (!sameYear) {
    // Cross-year: each end carries its own year regardless of currentYear.
    return ja
      ? `${single(s, true, true)}〜${single(e, true, true)}`
      : `${single(s, false, true)} – ${single(e, false, true)}`;
  }

  if (ja) {
    const yearPrefix = showYear ? `${s.y}年` : '';
    return sameMonth
      ? `${yearPrefix}${s.m}月${s.d}〜${e.d}日`
      : `${yearPrefix}${s.m}月${s.d}日〜${e.m}月${e.d}日`;
  }
  const yearSuffix = showYear ? ` ${s.y}` : '';
  return sameMonth
    ? `${EN_MONTHS[s.m - 1]} ${s.d}–${e.d}${yearSuffix}`
    : `${EN_MONTHS[s.m - 1]} ${s.d} – ${EN_MONTHS[e.m - 1]} ${e.d}${yearSuffix}`;
}
